/**
 * Select — shared types, Zod prop schema, styling constants, colour helpers
 * and the keyboard highlight helpers.
 *
 * A single-choice dropdown: one field shell (floating label, the picked
 * option's label and a down chevron) that opens a list of options below it.
 * It is the WAI-ARIA select-only combobox. Not to be confused with
 * SingleSelect, which shows every choice as an option card.
 *
 * Source: Figma file hk9NaJrKRwkoGmmZ5rxbsE, page v7, "Web/Select"
 * (node 69:221). The v7 sheet was drawn from this package's code rather than
 * the UI Design System library, so its variable names stand until design adds
 * them to the library.
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { radius } from '../../elements/radius';
import { shadows } from '../../elements/shadows';
import type { ShadowDefinition } from '../../elements/shadows';
import { sizing } from '../../elements/sizing';
import { spacing } from '../../elements/spacing';
import { states } from '../../elements/states';
import { typography } from '../../elements/typography';
import type { TypeStyle } from '../../elements/typography';
import type { IconName } from '../../elements/icons/registry';
import { TEXT_INPUT_BORDER_RADIUS, TEXT_INPUT_HEIGHT } from '../TextInput/TextInput';

export const selectChoiceSchema = z.object({
  /** What the field reports when this option is picked. */
  value: z.string(),
  /** What the person sees. Caller-supplied copy. */
  label: z.string(),
});

export type SelectChoice = z.infer<typeof selectChoiceSchema>;

/** Catalog-facing schema: data props only; callbacks are platform props. */
export const selectPropsSchema = z.object({
  label: z.string().optional(),
  options: z.array(selectChoiceSchema),
  /** The picked option's `value`. */
  value: z.string().optional(),
  errorText: z.string().optional(),
  disabled: z.boolean().default(false),
  testID: z.string().optional(),
  accessibilityLabel: z.string().optional(),
});

export type SelectProps = z.infer<typeof selectPropsSchema>;

// ---------------------------------------------------------------------------
// Highlight helpers
// ---------------------------------------------------------------------------

/** The option to highlight on opening: the picked one, else the first. */
export function selectInitialIndex(options: readonly SelectChoice[], value?: string): number {
  if (options.length === 0) return -1;
  const picked = options.findIndex((option) => option.value === value);
  return picked >= 0 ? picked : 0;
}

/** Moves the highlight by `step`, wrapping at both ends. */
export function selectStepIndex(count: number, index: number, step: 1 | -1): number {
  if (count === 0) return -1;
  if (index < 0) return step === 1 ? 0 : count - 1;
  return (index + step + count) % count;
}

/**
 * The next option after `index` whose label starts with `character`, wrapping,
 * or `index` unchanged when none does.
 */
export function selectTypeaheadIndex(
  options: readonly SelectChoice[],
  index: number,
  character: string,
): number {
  const wanted = character.toLocaleLowerCase();
  for (let offset = 1; offset <= options.length; offset += 1) {
    const candidate = (index + offset + options.length) % options.length;
    const label = options[candidate]?.label.trimStart().toLocaleLowerCase() ?? '';
    if (label.startsWith(wanted)) return candidate;
  }
  return index;
}

/** The label floats to a caption once an option is picked. */
export function isSelectLabelFloating(picked: boolean): boolean {
  return picked;
}

// ---------------------------------------------------------------------------
// Styling constants — all derived from design-system elements
// ---------------------------------------------------------------------------

export const SELECT_HEIGHT = TEXT_INPUT_HEIGHT;
export const SELECT_BORDER_RADIUS = TEXT_INPUT_BORDER_RADIUS;
export const SELECT_BORDER_WIDTH = states.borderWidth.default;
export const SELECT_HORIZONTAL_PADDING = spacing.m;
/** Gap between the text column and the chevron. */
export const SELECT_SHELL_GAP = spacing.s;
export const SELECT_LABEL_VALUE_GAP = spacing.xxs;
export const SELECT_PANEL_GAP = spacing.s;
export const SELECT_ERROR_GAP = spacing.s;
export const SELECT_ERROR_INDENT = spacing.m;

export const SELECT_CHEVRON_ICON: IconName = 'down-chevron';
export const SELECT_CHEVRON_SIZE = sizing.icon.xs;
// v7 keeps the chevron dark in every state, Disabled included.
export const SELECT_CHEVRON_COLOR = colors.icon.primary;

export const SELECT_VALUE_TYPOGRAPHY: TypeStyle = typography.b2Regular;
export const SELECT_RESTING_LABEL_TYPOGRAPHY: TypeStyle = typography.b2Regular;
export const SELECT_FLOATING_LABEL_TYPOGRAPHY: TypeStyle = typography.c2Regular;

export const SELECT_PANEL_BACKGROUND = colors.background.container;
export const SELECT_PANEL_BORDER_COLOR = colors.stroke.divider;
export const SELECT_PANEL_BORDER_WIDTH = states.borderWidth.default;
export const SELECT_PANEL_RADIUS = radius.xl;
export const SELECT_PANEL_SHADOW: ShadowDefinition = shadows.shadow400;
/**
 * Stacking order that lifts the open list over the content after the field.
 * No layering element exists yet (a flagged gap); one level above the page's
 * default is all the list needs.
 */
export const SELECT_PANEL_LAYER = 1;

export const SELECT_OPTION_HORIZONTAL_PADDING = spacing.lg;
export const SELECT_OPTION_VERTICAL_PADDING = spacing.m;
export const SELECT_OPTION_TYPOGRAPHY: TypeStyle = typography.b2Regular;
export const SELECT_OPTION_TEXT_COLOR = colors.text.primary;
export const SELECT_OPTION_BACKGROUND = colors.background.container;
export const SELECT_OPTION_HIGHLIGHT_BACKGROUND = colors.background.secondary;

// ---------------------------------------------------------------------------
// Colour helpers
// ---------------------------------------------------------------------------

export interface SelectState {
  disabled: boolean;
  hasError: boolean;
  focused: boolean;
  hovered: boolean;
  open: boolean;
}

export interface SelectColors {
  background: string;
  border: string;
  label: string;
  value: string;
}

export function selectColorsFor({
  disabled,
  hasError,
  focused,
  hovered,
  open,
}: SelectState): SelectColors {
  // v7's `stroke/alert` error border; `button.critical` holds the same colour
  // as `stroke.alert`.
  const border = disabled
    ? colors.stroke.divider
    : hasError
      ? colors.button.critical
      : focused || hovered || open
        ? colors.stroke.primary
        : colors.stroke.input;
  return {
    background: disabled ? colors.background.secondary : colors.background.container,
    border,
    label: disabled ? colors.text.tertiary : colors.text.secondary,
    value: disabled ? colors.text.tertiary : colors.text.primary,
  };
}
